import { useEffect, useId, useRef, useState } from 'react';

import Tooltip from '@/designSystem/overlays/Tooltip';

import {
  ICON_HOVER_TEXT_TOOLTIP_SURFACE_CLASS,
  ICON_HOVER_TEXT_TRIGGER_ICON_CLASS,
} from './iconHoverTextClasses.js';

/**
 * Info icon with portaled hover/focus tooltip.
 *
 * @param {object} props
 * @param {import('react').ReactNode} props.icon - Trigger icon (often `UnifiedIcon` with `info-icon`).
 * @param {import('react').ReactNode} props.text - Tooltip body copy.
 * @param {number} [props.width=12] - Fallback width token when `className` is omitted.
 * @param {number} [props.height=12] - Fallback height token when `className` is omitted.
 * @param {string} [props.className=''] - Size/layout classes for the trigger wrapper.
 * @returns {JSX.Element}
 */
export function IconHoverText({ icon, text, width = 12, height = 12, className = '' }) {
  const [showText, setShowText] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, offsetX: -12, offsetY: -40 });
  const tooltipId = useId();

  const targetRef = useRef(null);

  useEffect(() => {
    if (showText && targetRef.current) {
      const calculateTooltipPosition = () => {
        const target = targetRef.current;
        const targetRect = target.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        const viewportWidth = window.innerWidth;

        const x = targetRect.left + scrollLeft + targetRect.width / 2;
        const y = targetRect.top + scrollTop;

        const isInRightHalf = targetRect.left > viewportWidth / 2;

        let offsetX;
        let offsetY;

        if (isInRightHalf) {
          offsetX = -220;
        } else {
          offsetX = 20;
        }

        offsetY = -50;

        if (targetRect.top < 80) {
          offsetY = 20;
        }

        setTooltipPosition({ x, y, offsetX, offsetY });
      };

      calculateTooltipPosition();

      window.addEventListener('scroll', calculateTooltipPosition, true);
      window.addEventListener('resize', calculateTooltipPosition);

      return () => {
        window.removeEventListener('scroll', calculateTooltipPosition, true);
        window.removeEventListener('resize', calculateTooltipPosition);
      };
    }
  }, [showText]);

  const sizeMap = {
    4: 'w-4 h-4',
    5: 'w-5 h-5',
    6: 'w-6 h-6',
    8: 'w-8 h-8',
    12: 'w-12 h-12',
  };

  const sizeClass = className || sizeMap[width] || sizeMap[height] || `w-${width} h-${height}`;

  return (
    <>
      <div
        className={[
          sizeClass,
          'relative flex shrink-0 items-center justify-center hover:cursor-pointer',
          ICON_HOVER_TEXT_TRIGGER_ICON_CLASS,
        ].join(' ')}
        tabIndex="0"
        onMouseEnter={() => setShowText(true)}
        onMouseLeave={() => setShowText(false)}
        onClick={() => setShowText(!showText)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setShowText(!showText);
          if (e.key === 'Escape') setShowText(false);
        }}
        onBlur={() => setShowText(false)}
        aria-describedby={showText ? tooltipId : undefined}
        ref={targetRef}
        data-testid="components-icon-hover-text-trigger"
      >
        {icon}
      </div>

      {showText && (
        <Tooltip
          x={tooltipPosition.x}
          y={tooltipPosition.y}
          offsetX={tooltipPosition.offsetX}
          offsetY={tooltipPosition.offsetY}
          animated={true}
        >
          <div
            id={tooltipId}
            role="tooltip"
            data-testid="components-icon-hover-text-tooltip"
            className={[
              'w-max max-w-[25ch] p-2 text-center',
              ICON_HOVER_TEXT_TOOLTIP_SURFACE_CLASS,
            ].join(' ')}
          >
            {text}
          </div>
        </Tooltip>
      )}
    </>
  );
}

export default IconHoverText;
